import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Award, Users, Globe, TrendingUp, CheckCircle, ArrowRight } from 'lucide-react';

const TEAM_IMG = 'https://images.unsplash.com/photo-1752159400890-d906038f1b35?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMG1lZXRpbmclMjBoYW5kc2hha2UlMjBjb3Jwb3JhdGUlMjBzaWxob3VldHRlfGVufDB8fHx8MTc3MDE3MzMwMnww&ixlib=rb-4.1.0&q=85';
const MEETING_IMG = 'https://images.unsplash.com/photo-1758599543152-a73184816eba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMG1lZXRpbmclMjBoYW5kc2hha2UlMjBjb3Jwb3JhdGUlMjBzaWxob3VldHRlfGVufDB8fHx8MTc3MDE3MzMwMnww&ixlib=rb-4.1.0&q=85';

const values = [
  {
    icon: Award,
    title: 'Excellence',
    description: 'We strive for excellence in every aspect of our business, from product quality to customer service.'
  },
  {
    icon: Users,
    title: 'Partnership',
    description: 'We believe in building long-term relationships with our clients based on trust and mutual success.'
  },
  {
    icon: Globe,
    title: 'Global Reach',
    description: 'With partners across 50+ countries, we bring the world of electronics to your doorstep.'
  },
  {
    icon: TrendingUp,
    title: 'Innovation',
    description: 'We stay ahead of market trends to bring you the latest and most sought-after products.'
  }
];

const milestones = [
  'Founded in London, UK',
  'Expanded to 20+ countries',
  'Partnership with major distributors',
  'Reached 500+ active partners',
  '10,000+ products in catalogue'
];

export default function AboutPage() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
  };

  return (
    <main className="pt-32" data-testid="about-page">
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
        >
          <div>
            <span className="inline-block px-4 py-2 rounded-full bg-[#0096D6]/10 text-[#0096D6] font-manrope text-sm font-medium mb-4">
              About Us
            </span>
            <h1 className="font-outfit text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Your Trusted Partner in
              <span className="block gradient-text-cyan">Wholesale Electronics</span>
            </h1>
            <p className="font-manrope text-slate-600 text-lg leading-relaxed mb-8">
              Based in London, Real Tech Services Ltd is a leading wholesaler of consumer electronics, 
              serving businesses worldwide with brand-new products across audio, wearables, mobile devices, 
              and computing components.
            </p>
            <p className="font-manrope text-slate-600 text-lg leading-relaxed mb-8">
              We work closely with some of the world&apos;s leading distributors and wholesalers to ensure 
              quality, reliability, and consistent supply for our partners.
            </p>
            <Link
              to="/contact"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="about-cta-contact"
            >
              Partner With Us
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative z-10"
            >
              <img
                src={TEAM_IMG}
                alt="Business Partnership"
                className="rounded-2xl shadow-2xl w-full"
              />
            </motion.div>
            <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-[#0096D6]/10 rounded-2xl -z-10" />
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-slate-100 rounded-2xl -z-10" />
          </div>
        </motion.div>
      </section>

      {/* Values Section */}
      <section className="bg-slate-50 section-padding">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 rounded-full bg-[#0096D6]/10 text-[#0096D6] font-manrope text-sm font-medium mb-4">
              Our Values
            </span>
            <h2 className="font-outfit text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              What Drives Us
            </h2>
            <p className="font-manrope text-slate-600 text-lg max-w-2xl mx-auto">
              Our core values guide everything we do, from product selection to customer relationships.
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {values.map((value, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="opulent-card p-8 text-center"
                data-testid={`value-card-${index}`}
              >
                <div className="w-16 h-16 rounded-2xl bg-[#0096D6]/10 flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-[#0096D6]" />
                </div>
                <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">
                  {value.title}
                </h3>
                <p className="font-manrope text-slate-600">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src={MEETING_IMG}
                alt="Business Meeting"
                className="rounded-2xl shadow-xl w-full"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-2 rounded-full bg-[#0096D6]/10 text-[#0096D6] font-manrope text-sm font-medium mb-4">
                Our Journey
              </span>
              <h2 className="font-outfit text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                A Legacy of Excellence
              </h2>
              <p className="font-manrope text-slate-600 text-lg leading-relaxed mb-8">
                From our humble beginnings in London, we&apos;ve grown to become a trusted name in wholesale 
                electronics distribution. Our journey has been marked by continuous growth, strong 
                partnerships, and an unwavering commitment to quality.
              </p>

              <div className="space-y-4">
                {milestones.map((milestone, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-[#0096D6] flex-shrink-0" />
                    <span className="font-manrope text-slate-700">{milestone}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-950 py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Start a Partnership?
            </h2>
            <p className="font-manrope text-white/70 text-lg mb-8">
              Join our network of successful partners and unlock access to premium wholesale electronics.
            </p>
            <Link
              to="/contact"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="about-footer-cta"
            >
              Get in Touch
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
